import { PROJECT_SPECIFICATION_FORM_RULES } from '../project-type-rules/specification-form-rules';
import {
  RuleOverlay,
  SpecificationFormRule,
  SpecificationFormRuleField,
  SpecificationFormRuleFields
} from '../project-type-rules/specification-form-rule';
import {
  ProjectCreationWorkflow,
  RuleResolutionContext,
  WorkflowConfigurationError,
  WorkflowProgramRef
} from './project-creation-workflow.model';

/**
 * Rule resolution for the project-creation form (design FR-1, FR-13, FR-14).
 *
 * Deliberately Angular-free and dependency-free so it can run under `npm run test:pure`. The
 * Angular service is a thin delegate; all the logic worth testing lives here.
 */

/**
 * The rule used before a project type is known — a `providesProjectType` workflow before its
 * provider has been confirmed.
 *
 * `resolveRule` must NEVER return null. `name` and `program` are themselves rule-driven fields:
 * `createForm` adds `nameControl`/`programControl` only when their `display` is true, and the
 * template gates on the same flags. A null rule therefore builds a form with neither control and
 * stage one cannot render at all.
 */
export const STAGE_ONE_RULE: SpecificationFormRule = Object.freeze({
  fields: {
    name: { display: true, required: true, headingKey: 'project-name' },
    program: { display: true, required: true },
    projectType: { display: false, required: false },
    description: { display: false, required: false },
    postalCode: { display: false, required: false },
    address1: { display: false, required: false },
    address2: { display: false, required: false },
    map: { display: false, required: false },
    country: { display: false, required: false },
    state: { display: false, required: false },
    city: { display: false, required: false },
    latitude: { display: false, required: false },
    longitude: { display: false, required: false },
    currency: { display: false, required: false },
    exchangeRate: { display: false, required: false },
    cod: { display: false, required: false },
    tenderProject: { display: false, required: false },
    status: { display: false, required: false }
  },
  keys: { header: 'site-location', info: 'basic-info', location: 'project-location', currency: 'tariff' }
} as SpecificationFormRule);

/** Field pairs that must be hidden together or not at all (FR-14). */
const COUPLED_FIELDS: [keyof SpecificationFormRuleFields, keyof SpecificationFormRuleFields][] = [
  ['currency', 'exchangeRate'],
  ['country', 'state']
];

/**
 * Deep-copies a base rule so resolution can never mutate it.
 *
 * The eight base rules are module-load singletons handed out by reference. Merging in place would
 * leak a `contextOverlay` write — a `readOnly` from one locked-program form — into every other form
 * for the rest of the session.
 */
const cloneRule = (rule: SpecificationFormRule): SpecificationFormRule => {
  const fields = {} as SpecificationFormRuleFields;
  Object.keys(rule.fields).forEach(key => {
    fields[key] = { ...rule.fields[key] };
  });
  return { fields, keys: { ...rule.keys } };
};

/**
 * `⊕` — merges an overlay into a rule, PER FIELD and PER PROPERTY.
 *
 * `{ ...base[field], ...overlay[field] }`, so an overlay entry replaces only the properties it
 * names and leaves the rest of the base entry intact. An overlay saying `{ readOnly: true }` keeps
 * whatever `display`/`required` the base rule declared, and continues to track it if the base table
 * later changes.
 */
const applyOverlay = (rule: SpecificationFormRule, overlay: RuleOverlay): SpecificationFormRule => {
  if (!overlay) {
    return rule;
  }
  Object.keys(overlay).forEach(key => {
    const patch = overlay[key] as Partial<SpecificationFormRuleField>;
    if (!patch) {
      return;
    }
    rule.fields[key] = { ...(rule.fields[key] ?? { display: false, required: false }), ...patch };
  });
  return rule;
};

/**
 * `providedNull` means "this field does not apply under this workflow" — hide it and drop its
 * requiredness. Distinct from `display: false`, which is reversible: a later program switch
 * resurfaces a hidden field, and would resurface it required-and-blank on a project that
 * legitimately has no value for it.
 */
const applyProvidedNull = (rule: SpecificationFormRule): SpecificationFormRule => {
  Object.keys(rule.fields).forEach(key => {
    if (rule.fields[key]?.providedNull) {
      rule.fields[key] = { ...rule.fields[key], display: false, required: false };
    }
  });
  return rule;
};

/** Context-driven overlay: the two cases that are neither program config nor workflow code. */
const contextOverlay = (
  program: WorkflowProgramRef | null | undefined,
  context: RuleResolutionContext | null | undefined
): RuleOverlay => {
  const overlay: RuleOverlay = {};
  if (context?.programLocked) {
    overlay.program = { readOnly: true };
  }
  if (program?.country?.id) {
    overlay.country = { readOnly: true, fixedValue: program.country.id };
  }
  return overlay;
};

export interface ResolveRuleResult {
  rule: SpecificationFormRule;
  /** Set when the program names a workflow that cannot be honored; blocks the form. */
  configurationError?: WorkflowConfigurationError;
}

/**
 * Resolves the field rule for a form.
 *
 * `baseRule(projectType)` ⊕ `workflow.ruleOverlay` ⊕ `contextOverlay(program, context)`.
 *
 * A program with no workflow key and no fixed country resolves to a rule DEEP-EQUAL to the base
 * rule for that project type — no existing program changes behavior (FR-13).
 *
 * @param projectType the project type, or null/undefined when not yet known
 * @param program the authoritative program object, or null for "No Program"
 * @param context non-field rendering context
 * @param registry the workflow registry; empty in production until a workflow is registered
 */
export const resolveRule = (
  projectType: string | null | undefined,
  program: WorkflowProgramRef | null | undefined,
  context: RuleResolutionContext | null | undefined,
  registry: ProjectCreationWorkflow[] = []
): ResolveRuleResult => {
  const key = program?.projectCreationWorkflowKey;
  const workflow = key ? registry.find(candidate => candidate.key === key) : undefined;

  // An unknown key must never block ordinary project creation — fall back to the base rule and warn.
  if (key && !workflow) {
    // eslint-disable-next-line no-console
    console.warn(
      `[project-creation] Program names unknown workflow key "${key}"; falling back to the base rule.`
    );
  }

  // A registered workflow that needs the program's country cannot prefill a location without one,
  // and the form it would render cannot be submitted (country is required). Blocking here turns a
  // silent dead end into a sentence on screen. Unlike the unknown-key case this does NOT depend on
  // the project type: the workflow is present and honorable, the program is simply incomplete.
  const missingCountry: WorkflowConfigurationError | undefined =
    workflow?.requiresProgramCountry && !program?.country?.id
      ? {
        workflowKey: workflow.key,
        reason: 'PROGRAM_HAS_NO_COUNTRY',
        message:
          `This program is set up for the "${workflow.key}" project-creation workflow, which ` +
          `fills in the location from the program's country — but this program has no country ` +
          `set. A programme administrator needs to set it before projects can be created here.`
      }
      : undefined;

  if (!projectType) {
    // No type yet. If a registered workflow was going to supply it, hold at stage one. If the key
    // is unknown there is nothing to supply it and no type to fall back to, so this is a blocking
    // configuration error rather than a half-built form.
    if (key && !workflow) {
      return {
        rule: cloneRule(STAGE_ONE_RULE),
        configurationError: {
          workflowKey: key,
          reason: 'UNKNOWN_KEY_PROVIDES_PROJECT_TYPE',
          message:
            `This program is configured for a project-creation workflow ("${key}") that is not ` +
            `available in this version of the application. Please contact support.`
        }
      };
    }
    // DT-6472 FR-29: a registered providesProjectType workflow may reshape stage one (VIDA
    // hides the name until confirm). Clone first — the singleton is frozen — and merge the
    // stage-one overlay only; the full ruleOverlay waits for the type.
    if (workflow?.providesProjectType && workflow.stageOneRuleOverlay) {
      return {
        rule: applyOverlay(cloneRule(STAGE_ONE_RULE), workflow.stageOneRuleOverlay),
        configurationError: missingCountry
      };
    }
    return { rule: cloneRule(STAGE_ONE_RULE), configurationError: missingCountry };
  }

  const base = PROJECT_SPECIFICATION_FORM_RULES[projectType];
  if (!base) {
    return { rule: cloneRule(STAGE_ONE_RULE), configurationError: missingCountry };
  }

  let rule = cloneRule(base);
  rule = applyOverlay(rule, workflow?.ruleOverlay);
  rule = applyOverlay(rule, contextOverlay(program, context));
  rule = applyProvidedNull(rule);
  return { rule, configurationError: missingCountry };
};

/**
 * Coupling invariant (FR-14): an overlay must not NEWLY break a field pair that the base rule had
 * symmetric.
 *
 * Note this is relative to the base, not absolute — `CiSiteRule` already ships
 * `currency: display true` with `exchangeRate: display false`, so an absolute "these must match"
 * check would flag a shipped base rule as broken. The pairs matter because three call sites
 * dereference a partner control unguarded (`showExchangeRates` → `currencyControl`,
 * `onCurrencyChanged` → `exchangeRateControl`) or gate on the partner's flag (`onCountryChanged`
 * on `state.display`). Those call sites are hardened in FM4; this check stops a workflow from
 * introducing a new asymmetry the hardening was not written for.
 *
 * @returns the pairs an overlay newly desynchronized; empty when well-formed
 */
export const findBrokenCouplings = (
  resolved: SpecificationFormRule,
  base: SpecificationFormRule
): [keyof SpecificationFormRuleFields, keyof SpecificationFormRuleFields][] =>
  COUPLED_FIELDS.filter(([left, right]) => {
    const baseSymmetric = !!base.fields[left]?.display === !!base.fields[right]?.display;
    const resolvedSymmetric = !!resolved.fields[left]?.display === !!resolved.fields[right]?.display;
    return baseSymmetric && !resolvedSymmetric;
  });

/** The unmodified base rule for a project type, for callers that need to diff against it. */
export const baseRuleFor = (projectType: string): SpecificationFormRule | undefined =>
  PROJECT_SPECIFICATION_FORM_RULES[projectType];
